// src/modules/leads/DirectCall.js
//
// Public page at /direct-call. Opened from a pre-saved URL carrying
// ?to=&from=&leadId=&k=[&notes=]. Immediately places a click-to-call via
// the key-gated backend endpoint and shows a single confirmation card.
// No login, no modal, no confirmation click.

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FaPhoneAlt, FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { initiateDirectCall, isValidPhoneNumber } from '../../services/communicationService';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing(3)};
  background: ${props => props.theme.colors.background};
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(4)};
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
`;

const Title = styled.h2`
  margin: 0 0 ${props => props.theme.spacing(2)} 0;
  color: ${props => props.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
`;

const Subtitle = styled.p`
  margin: 0 0 ${props => props.theme.spacing(3)} 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
`;

const NumberRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${props => props.theme.spacing(1)} 0;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray};
  font-size: ${props => props.theme.typography.fontSize.small};

  &:last-child {
    border-bottom: none;
  }

  span:first-child {
    color: ${props => props.theme.colors.textSecondary};
  }
  span:last-child {
    color: ${props => props.theme.colors.textPrimary};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
  }
`;

const StatusBox = styled.div`
  margin-top: ${props => props.theme.spacing(3)};
  padding: ${props => props.theme.spacing(3)};
  border-radius: ${props => props.theme.borderRadius};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  background: ${props => {
    if (props.$variant === 'success') return `${props.theme.colors.success}22`;
    if (props.$variant === 'error') return `${props.theme.colors.error}22`;
    return props.theme.colors.background;
  }};
  color: ${props => {
    if (props.$variant === 'success') return props.theme.colors.success;
    if (props.$variant === 'error') return props.theme.colors.error;
    return props.theme.colors.textPrimary;
  }};

  svg {
    font-size: 1.4rem;
    flex-shrink: 0;
  }
`;

const Spinner = styled(FaSpinner)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const RetryButton = styled.button`
  margin-top: ${props => props.theme.spacing(3)};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.black};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(3)};
  cursor: pointer;
  font-weight: ${props => props.theme.typography.fontWeight.medium};

  &:hover {
    opacity: 0.9;
  }
`;

const formatForDisplay = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const n = cleaned.slice(1);
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const hashParams = (to, from, leadId) => `${to}|${from}|${leadId}`;

const DirectCall = () => {
  const [searchParams] = useSearchParams();
  const to = searchParams.get('to');
  const from = searchParams.get('from');
  const leadId = searchParams.get('leadId');
  const k = searchParams.get('k');
  const notes = searchParams.get('notes') || '';

  const [status, setStatus] = useState('validating'); // validating | loading | success | error
  const [message, setMessage] = useState('');
  const firedRef = useRef(false);

  const placeCall = async () => {
    setStatus('loading');
    setMessage('');

    // Dedupe accidental refreshes within a short window for the same params.
    const dedupeKey = `directCall:${hashParams(to, from, leadId)}`;
    const last = Number(sessionStorage.getItem(dedupeKey) || 0);
    if (Date.now() - last < 60 * 1000) {
      setStatus('success');
      setMessage('Call already requested moments ago. If your phone did not ring, close this tab and use the CRM directly.');
      return;
    }

    try {
      await initiateDirectCall({ to_number: to, callback_number: from, lead_id: leadId, notes, k });
      sessionStorage.setItem(dedupeKey, String(Date.now()));
      setStatus('success');
      setMessage('Call request sent. Your phone will ring momentarily.');
    } catch (err) {
      setStatus('error');
      if (err.status === 401) {
        setMessage('This direct-call link is invalid. The key may have been rotated — generate a new link from your CRM settings.');
      } else if (err.status === 400) {
        setMessage(err.message || 'The link is missing required information.');
      } else if (err.status === 429) {
        setMessage('Too many calls in a short time. Please wait a moment and try again.');
      } else {
        setMessage(err.message || 'Something went wrong placing the call. Try again or use the CRM directly.');
      }
    }
  };

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    if (!to || !from || !leadId || !k) {
      setStatus('error');
      setMessage('Missing required parameters. The link must include to, from, leadId, and k.');
      return;
    }
    if (!isValidPhoneNumber(to) || !isValidPhoneNumber(from)) {
      setStatus('error');
      setMessage('One of the phone numbers in the link is not a valid US number.');
      return;
    }

    placeCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    firedRef.current = true;
    placeCall();
  };

  return (
    <Page>
      <Card>
        <Title>
          <FaPhoneAlt />
          Direct Call
        </Title>
        <Subtitle>Placing a click-to-call. Your phone will ring first, then bridge to the lead.</Subtitle>

        {to && from && (
          <>
            <NumberRow><span>To (lead)</span><span>{formatForDisplay(to)}</span></NumberRow>
            <NumberRow><span>From (your phone)</span><span>{formatForDisplay(from)}</span></NumberRow>
          </>
        )}

        {status === 'validating' || status === 'loading' ? (
          <StatusBox $variant="loading">
            <Spinner />
            <div>{status === 'validating' ? 'Validating link…' : 'Connecting you now…'}</div>
          </StatusBox>
        ) : null}

        {status === 'success' && (
          <StatusBox $variant="success">
            <FaCheckCircle />
            <div>{message}</div>
          </StatusBox>
        )}

        {status === 'error' && (
          <>
            <StatusBox $variant="error">
              <FaExclamationTriangle />
              <div>{message}</div>
            </StatusBox>
            <RetryButton onClick={handleRetry}>Try again</RetryButton>
          </>
        )}
      </Card>
    </Page>
  );
};

export default DirectCall;
